import {
  BlobCtaButton,
  CardGlass,
  ErrorPage,
  LoadingSpinner,
  SelectField,
  ThemeToggle,
  ThemeBackground,
  ThemeProvider,
  ToastProvider,
  Typography,
  useToast,
} from "@cupcodev/ui";
import { useCallback, useEffect, useState } from "react";
import { createRoot, type Root } from "react-dom/client";
import "@cupcodev/ui/styles";
import { I18nProvider, useI18n } from "./app/i18n/I18nProvider";
import { productionSessionClient } from "./composition/production-session-client";
import { naveBrandAssets } from "./app/branding/telescup-brand-assets";
import { App as CustomerApplication } from "./app/App";
import { RealAppProviders } from "./app/providers/RealAppProviders";
import { NaveIcon } from "./ui/NavePage";
import type { NaveSessionBootstrap, NaveSessionClient } from "./infrastructure/auth/nave-session-client";
import "./styles.css";

const rootElement = document.getElementById("root");
if (rootElement === null) throw new Error("Nave root element not found");

const root: Root = import.meta.hot?.data.root ?? createRoot(rootElement);
if (import.meta.hot) import.meta.hot.data.root = root;

type SessionState = "AUTH_RESOLVING" | "UNAUTHENTICATED" | "AUTHENTICATED" | "AUTH_ERROR";

const PublicHome = ({ onLogin, returnTo, loginUnavailable }: { onLogin(): void; returnTo: string; loginUnavailable: boolean }) => {
  const { t } = useI18n();
  const { toast } = useToast();
  useEffect(() => {
    if (!loginUnavailable) return;
    toast({ variant: "error", title: t("publicHome.loginUnavailable") });
    const url = new URL(window.location.href);
    url.searchParams.delete("authError");
    window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
    // Fire once per redirect; toast/t identities are not stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginUnavailable]);
  return (
    <>
      <ThemeBackground enableStars />
      <div className="nave-public-page">
      <header className="nave-public-navbar">
        <a href="/" aria-label={t("navigation.home")}><img src={naveBrandAssets.horizontalLogo} alt="Nave · Cupcode" /></a>
        <nav aria-label={t("user.menu")} className="nave-public-nav-actions">
          <ThemeToggle />
          <details className="nave-public-account-menu">
            <summary aria-label={t("user.menu")}>
              <img src={naveBrandAssets.accountMenu} alt="" width={44} height={44} />
            </summary>
            <div className="nave-public-account-panel"><BlobCtaButton size="md" type="button" onClick={onLogin}><img className="nave-sign-in-icon" src={naveBrandAssets.cupcodeIcon} alt="" width={24} height={24} />{t("publicHome.signIn")}</BlobCtaButton></div>
          </details>
        </nav>
      </header>
      <main className="nave-public-home">
        <div className="nave-public-home-copy">
          <Typography as="h1" variant="display-lg">{t("publicHome.title")}</Typography>
          <Typography as="p" variant="body-lg" tone="muted">{t("publicHome.description")}</Typography>
          <ul className="nave-public-features">
            <li><NaveIcon name="lucide:rocket" />{t("publicHome.feature.projects")}</li>
            <li><NaveIcon name="lucide:receipt-text" />{t("publicHome.feature.billing")}</li>
            <li><NaveIcon name="lucide:life-buoy" />{t("publicHome.feature.support")}</li>
          </ul>
          <BlobCtaButton type="button" size="lg" onClick={onLogin}><img className="nave-sign-in-icon" src={naveBrandAssets.cupcodeIcon} alt="" width={24} height={24} />{t("publicHome.signIn")}</BlobCtaButton>
          {returnTo !== "/" ? <p className="nave-public-home-return" role="status">{t("publicHome.returnNotice")}</p> : null}
        </div>
        <div className="nave-public-home-mark" aria-hidden="true">
          <img src={naveBrandAssets.squareLogo} alt="" />
        </div>
      </main>
      </div>
    </>
  );
};

const ERROR_ROUTE_PREFIX = "/erro/";
const errorRoute = (code: string, returnTo = "/"): string => `${ERROR_ROUTE_PREFIX}${code}?returnTo=${encodeURIComponent(returnTo)}`;

type ErrorRouteAction = "retry" | "login" | "logout" | "home";
type ErrorRouteDefinition = Readonly<{ code: string; title: string; body: string; action: ErrorRouteAction; actionLabel: string }>;
// Dedicated error routes: a failing dependency or rejected login must never
// replace the public Home. Unknown codes resolve to 404.
const errorRoutes: Readonly<Record<string, ErrorRouteDefinition>> = {
  "401": { code: "401", title: "errors.invalidEntryContext.title", body: "errors.invalidEntryContext.body", action: "login", actionLabel: "publicHome.signIn" },
  "403": { code: "403", title: "errors.membershipRequired.title", body: "errors.membershipRequired.body", action: "home", actionLabel: "errors.notFound.back" },
  "404": { code: "404", title: "errors.notFound.title", body: "errors.notFound.body", action: "home", actionLabel: "errors.notFound.back" },
  "500": { code: "500", title: "errors.generic.title", body: "errors.generic.body", action: "retry", actionLabel: "common.retry" },
  "503": { code: "503", title: "errors.accountsUnavailable.title", body: "errors.accountsUnavailable.body", action: "retry", actionLabel: "common.retry" },
  "sem-acesso": { code: "403", title: "errors.noNaveAccess.title", body: "errors.noNaveAccess.body", action: "logout", actionLabel: "account.actions.signOut" },
  perfil: { code: "503", title: "production.profileUnavailable.title", body: "production.profileUnavailable.body", action: "logout", actionLabel: "account.actions.signOut" },
};

const RouteErrorPage = ({ routeCode }: { routeCode: string }) => {
  const { t } = useI18n();
  const definition = errorRoutes[routeCode] ?? errorRoutes["404"]!;
  const requested = new URLSearchParams(window.location.search).get("returnTo") ?? "/";
  const target = safeReturnTarget(requested.startsWith(ERROR_ROUTE_PREFIX) ? "/" : requested);
  const act = () => {
    if (definition.action === "login") window.location.assign(`/auth/login?returnTo=${encodeURIComponent(target)}`);
    else if (definition.action === "home") window.location.assign("/");
    else if (definition.action === "logout") void productionSessionClient.logout().then(() => window.location.replace("/"), () => window.location.assign("/"));
    else window.location.assign(target);
  };
  return <>
    <ThemeBackground enableStars />
    <main className="accounts-experience-main">
      <ErrorPage code={definition.code} title={t(definition.title as never)} description={t(definition.body as never)}
        actionLabel={t(definition.actionLabel as never)} onAction={act} />
    </main>
  </>;
};

const ProductionEntry = ({ sessionClient }: { sessionClient: NaveSessionClient }) => {
  const { t } = useI18n();
  const [state, setState] = useState<SessionState>("AUTH_RESOLVING");
  const [session, setSession] = useState<NaveSessionBootstrap | null>(null);
  const [switching, setSwitching] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [switchError, setSwitchError] = useState(false);
  const entryUrl = new URL(window.location.href);
  const loginUnavailable = entryUrl.searchParams.get("authError") === "unavailable";
  entryUrl.searchParams.delete("authError");
  const path = `${entryUrl.pathname}${entryUrl.search}${entryUrl.hash}`;
  const returnTo = safeReturnTarget(path);

  const resolveSession = useCallback(async (signal: AbortSignal) => {
    setState("AUTH_RESOLVING");
    try {
      const next = await sessionClient.read();
      if (!signal.aborted) {
        setSession(next);
        setState(next ? "AUTHENTICATED" : "UNAUTHENTICATED");
      }
    } catch {
      if (!signal.aborted) setState("AUTH_ERROR");
    }
  }, [sessionClient]);

  useEffect(() => {
    const controller = new AbortController();
    void resolveSession(controller.signal);
    return () => controller.abort();
  }, [attempt, resolveSession]);

  const login = () => {
    window.location.assign(`/auth/login?returnTo=${encodeURIComponent(returnTo)}`);
  };
  const logout = async () => {
    try {
      await sessionClient.logout();
      window.location.replace("/");
    } catch {
      setState("AUTH_ERROR");
    }
  };
  const switchOrganization = async (organizationId: string) => {
    if (switching || !session?.organizations.some((organization) => organization.id === organizationId)) return;
    setSwitching(true);
    setSwitchError(false);
    try {
      await sessionClient.switchOrganization(organizationId);
      const updated = await sessionClient.read();
      if (!updated) { setSession(null); setState("UNAUTHENTICATED"); }
      else setSession(updated);
    } catch {
      setSwitchError(true);
    } finally { setSwitching(false); }
  };

  const switchClient = async (clientId:string) => {
    if(switching || !sessionClient.switchClient) return;
    setSwitching(true);setSwitchError(false);
    try {await sessionClient.switchClient(clientId);const updated=await sessionClient.read();setSession(updated);
      if(!updated)setState("UNAUTHENTICATED");} catch{setSwitchError(true);} finally{setSwitching(false);}
  };

  if (state === "AUTH_RESOLVING") {
    return <><ThemeBackground enableStars /><main className="nave-auth-status" aria-busy="true" role="status"><LoadingSpinner label={t("state.loading")} /></main></>;
  }
  if (state === "UNAUTHENTICATED") return <PublicHome onLogin={login} returnTo={returnTo} loginUnavailable={loginUnavailable} />;
  if (state === "AUTH_ERROR") {
    window.location.replace(errorRoute("503", returnTo));
    return <><ThemeBackground enableStars /><main className="nave-auth-status" aria-busy="true" role="status"><LoadingSpinner label={t("state.loading")} /></main></>;
  }

  if (!session) {
    window.location.replace(errorRoute("perfil", returnTo));
    return <><ThemeBackground enableStars /><main className="nave-auth-status" aria-busy="true" role="status"><LoadingSpinner label={t("state.loading")} /></main></>;
  }

  if (!session.activeOrganization) return <>
    <ThemeBackground enableStars />
    <main className="accounts-experience-main"><CardGlass variant="glass-md" className="nave-real-card">
      <img className="nave-brand-wordmark" src={naveBrandAssets.horizontalLogo} alt="Nave · Cupcode" />
      <Typography as="h1" variant="title-lg">{t(session.organizations.length ? "production.organization.choose" : "production.organization.none")}</Typography>
      {session.organizations.length ? <SelectField value="" ariaLabel={t("organization.switch")} disabled={switching}
        options={session.organizations.map((item) => ({ value: item.id, label: item.name }))}
        onChange={(value) => { void switchOrganization(value); }} /> : null}
      <BlobCtaButton size="md" variant="secondary" onClick={() => { void logout(); }}>{t("account.actions.signOut")}</BlobCtaButton>
    </CardGlass></main>
  </>;
  return <RealAppProviders session={session} logout={logout} selectOrganization={switchOrganization} selectClient={switchClient}
    switching={switching} switchError={switchError}><CustomerApplication /></RealAppProviders>;

};

const safeReturnTarget = (value: string): string => {
  if (!value.startsWith("/") || value.startsWith("//") || value.includes("\\") || /[\r\n]/u.test(value)) return "/";
  return value;
};

const render = async (): Promise<void> => {
  const routeCode = window.location.pathname.startsWith(ERROR_ROUTE_PREFIX)
    ? window.location.pathname.slice(ERROR_ROUTE_PREFIX.length).replace(/\/+$/u, "") : undefined;
  const entry = routeCode !== undefined
    ? <RouteErrorPage routeCode={routeCode} />
    : <ProductionEntry sessionClient={productionSessionClient} />;
  root.render(<ThemeProvider><I18nProvider><ToastProvider>{entry}</ToastProvider></I18nProvider></ThemeProvider>);
};

void render();
